import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getActiveCohorts } from '../api/Cohort';
import { CohortId } from '../api/Cohort/types/Cohort';
import { Founder } from '../api/founders';
import { Mentor, MentorId } from '../api/mentors';
import mentorsAPI from '../api/mentors';
import { Role } from '../api/user/Role';
import venturesAPI from '../api/ventures';
import { Venture, VentureId } from '../api/ventures/types/Venture';
import { SnackMessage } from '../components/common';
import { useAuth } from '../hooks';
import { fullName } from '../utils/String';
import { massAsyncRequest } from '../utils/api';
import { useResourceBundles } from './resource-bundles-context';
import { UserContext } from './user-context';

export interface VenturesContextProps {
  ventures: Venture[];
  mentor?: Mentor;
  cohorts: Array<{ label: string; value: CohortId | null }>;
  selectedVentureIds: Venture['id'][];
  loadedMentorsById: { [x: string]: Mentor[] };
  loadedFoundersById: { [x: string]: Founder[] };
  isMentorsAssigningVenture: { [x: string]: boolean };
  isFoundersAssigningVenture: { [x: string]: boolean };
  isSendingProfileUpdate: boolean;
  mentorsAssignmentTargetVenture: Venture | null;
  foundersAssignmentTargetVenture: Venture | null;
  reportLinkTargetVentureIds: VentureId[] | null;
  profileUpdateTargetVentureIds: VentureId[] | null;
  schedulingWizardTargetVenture: Venture | null;
  showMentorSchedulingWarning: boolean;
  setVentures: Dispatch<SetStateAction<Venture[]>>;
  setMentor: Dispatch<SetStateAction<Mentor | undefined>>;
  setCohorts: Dispatch<SetStateAction<Array<CohortOption>>>;
  setSelectedVentureIds: Dispatch<SetStateAction<Venture['id'][]>>;
  setLoadedMentorsById: Dispatch<SetStateAction<{ [x: string]: Mentor[] }>>;
  setLoadedFoundersById: Dispatch<SetStateAction<{ [x: string]: Founder[] }>>;
  setIsMentorsAssigningVenture: Dispatch<
    SetStateAction<{ [x: string]: boolean }>
  >;
  setIsFoundersAssigningVenture: Dispatch<
    SetStateAction<{ [x: string]: boolean }>
  >;
  setIsSendingProfileUpdate: Dispatch<SetStateAction<boolean>>;
  handleVote: (id: VentureId) => void;
  sendVentureUpdate: (onClose: () => any) => Promise<void>;
  sendVentureUpdateProfiles: (onClose: () => {}) => Promise<void>;
  handleMentorsLoaded: (
    ventureId: Venture['id'],
  ) => (loadedMentors: Mentor[]) => void;
  handleFoundersLoaded: (
    ventureId: Venture['id'],
  ) => (loadedFounders: Founder[]) => void;
  assignMentorsSuccessForm: (
    ventureId: Venture['id'],
    handleModalClose: () => any,
  ) => (params: {
    assigned: string[];
    removed: string[];
    all: Mentor[];
  }) => any;
  assignFoundersSuccessForm: (
    ventureId: Venture['id'],
    handleModalClose: () => any,
  ) => (params: {
    assigned: string[];
    removed: string[];
    all: Founder[];
  }) => any;
  setMentorsAssignmentTargetVenture: Dispatch<SetStateAction<Venture | null>>;
  setFoundersAssignmentTargetVenture: Dispatch<SetStateAction<Venture | null>>;
  setReportLinkTargetVentureIds: Dispatch<SetStateAction<VentureId[] | null>>;
  setProfileUpdateTargetVentureIds: Dispatch<
    SetStateAction<VentureId[] | null>
  >;
  setSchedulingWizardTargetVenture: Dispatch<SetStateAction<Venture | null>>;
  setShowMentorSchedulingWarning: Dispatch<SetStateAction<boolean>>;
  checkSchedulingWizardForMentor: (venture: Venture) => void;
}

export const VenturesContext = createContext<VenturesContextProps>({
  ventures: [],
  mentor: undefined,
  cohorts: [],
  selectedVentureIds: [],
  loadedMentorsById: {},
  loadedFoundersById: {},
  isMentorsAssigningVenture: {},
  isFoundersAssigningVenture: {},
  isSendingProfileUpdate: false,
  mentorsAssignmentTargetVenture: null,
  foundersAssignmentTargetVenture: null,
  reportLinkTargetVentureIds: null,
  profileUpdateTargetVentureIds: null,
  schedulingWizardTargetVenture: null,
  showMentorSchedulingWarning: false,
  setVentures: () => {},
  setMentor: () => {},
  setCohorts: () => {},
  setSelectedVentureIds: () => {},
  setLoadedMentorsById: () => {},
  setLoadedFoundersById: () => {},
  setIsMentorsAssigningVenture: () => {},
  setIsFoundersAssigningVenture: () => {},
  setIsSendingProfileUpdate: () => {},
  handleVote: () => {},
  sendVentureUpdate: async () => {},
  sendVentureUpdateProfiles: async () => {},
  handleMentorsLoaded: () => () => {},
  handleFoundersLoaded: () => () => {},
  assignMentorsSuccessForm: () => () => {},
  assignFoundersSuccessForm: () => () => {},
  setMentorsAssignmentTargetVenture: () => {},
  setFoundersAssignmentTargetVenture: () => {},
  setReportLinkTargetVentureIds: () => {},
  setProfileUpdateTargetVentureIds: () => {},
  setSchedulingWizardTargetVenture: () => {},
  checkSchedulingWizardForMentor: () => {},
  setShowMentorSchedulingWarning: () => {},
});

interface VenturesProviderProps {
  children: ReactNode;
}

type CohortOption = { label: string; value: CohortId | null };

export const VenturesProvider = ({ children }: VenturesProviderProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { rb } = useResourceBundles();

  const { hasRole } = useContext(UserContext);
  const { tokenData } = useAuth();

  const isMentor = hasRole(Role.Mentor);

  const [ventures, setVentures] = useState<Venture[]>([]);
  const [mentor, setMentor] = useState<Mentor | undefined>(undefined);
  const [cohorts, setCohorts] = useState<Array<CohortOption>>([]);
  const [selectedVentureIds, setSelectedVentureIds] = useState<Venture['id'][]>(
    [],
  );
  const [loadedMentorsById, setLoadedMentorsById] = useState<{
    [x: string]: Mentor[];
  }>({});
  const [loadedFoundersById, setLoadedFoundersById] = useState<{
    [x: string]: Founder[];
  }>({});
  const [isMentorsAssigningVenture, setIsMentorsAssigningVenture] = useState<{
    [x: string]: boolean;
  }>({});
  const [isFoundersAssigningVenture, setIsFoundersAssigningVenture] = useState<{
    [x: string]: boolean;
  }>({});
  const [isSendingProfileUpdate, setIsSendingProfileUpdate] = useState(false);
  const [mentorsAssignmentTargetVenture, setMentorsAssignmentTargetVenture] =
    useState<Venture | null>(null);
  const [foundersAssignmentTargetVenture, setFoundersAssignmentTargetVenture] =
    useState<Venture | null>(null);
  const [reportLinkTargetVentureIds, setReportLinkTargetVentureIds] = useState<
    VentureId[] | null
  >(null);
  const [profileUpdateTargetVentureIds, setProfileUpdateTargetVentureIds] =
    useState<VentureId[] | null>(null);
  const [schedulingWizardTargetVenture, setSchedulingWizardTargetVenture] =
    useState<Venture | null>(null);
  const [showMentorSchedulingWarning, setShowMentorSchedulingWarning] =
    useState(false);

  const handleVote = useCallback(
    (id: VentureId) => {
      if (!mentor) {
        return;
      }

      const venture = ventures.find((v) => v.id === id);

      if (!venture) return;

      if (
        venture.mentorsVoted?.includes(
          fullName(mentor.firstName, mentor.lastName),
        )
      ) {
        mentorsAPI.unvoteVenture(mentor.id, id).then(() =>
          setVentures((ventures) =>
            ventures.map((v) =>
              v.id === id
                ? {
                    ...v,
                    mentorsVoted:
                      v.mentorsVoted?.filter(
                        (m) =>
                          m !== fullName(mentor.firstName, mentor.lastName),
                      ) ?? null,
                  }
                : v,
            ),
          ),
        );
      } else {
        mentorsAPI.voteVenture(mentor.id, mentor.tenantId, id).then(() =>
          setVentures((ventures) =>
            ventures.map((v) =>
              v.id === id
                ? {
                    ...v,
                    mentorsVoted: [
                      ...(v.mentorsVoted ?? []),
                      fullName(mentor.firstName, mentor.lastName),
                    ],
                  }
                : v,
            ),
          ),
        );
      }
    },
    [mentor, ventures],
  );

  const sendVentureUpdate = useCallback(
    async (onClose: () => any) => {
      if (!profileUpdateTargetVentureIds) {
        return;
      }

      try {
        setIsSendingProfileUpdate(true);
        await venturesAPI.sendVentureUpdateInvites(
          profileUpdateTargetVentureIds,
        );
        enqueueSnackbar('The profile update request was successfully sent', {
          variant: 'success',
        });
        setIsSendingProfileUpdate(false);
        onClose();
      } catch (e) {
        const messageError = (e as AxiosError).response?.data?.message;

        setIsSendingProfileUpdate(false);
        enqueueSnackbar(
          'An error occurred while sending profile update request. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        throw e;
      }
    },
    [profileUpdateTargetVentureIds, enqueueSnackbar],
  );

  const sendVentureUpdateProfiles = useCallback(
    async (onClose: () => {}) => {
      try {
        setIsSendingProfileUpdate(true);
        await venturesAPI.sendVentureUpdateInvites(selectedVentureIds);
        enqueueSnackbar('The profile update requests were successfully sent', {
          variant: 'success',
        });
        setIsSendingProfileUpdate(false);
        onClose();
        setSelectedVentureIds([]);
      } catch (e) {
        const messageError = (e as AxiosError).response?.data?.message;

        setIsSendingProfileUpdate(false);
        enqueueSnackbar(
          'An error occurred while sending profile update request. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        throw e;
      }
    },
    [enqueueSnackbar, selectedVentureIds],
  );

  const handleMentorsLoaded = useCallback(
    (ventureId: Venture['id']) => (loadedMentors: Mentor[]) => {
      setVentures((prevVentures) =>
        prevVentures.map((venture) =>
          venture.id === ventureId
            ? { ...venture, assignedMentors: loadedMentors.length }
            : venture,
        ),
      );
      setLoadedMentorsById((prevState) => ({
        ...prevState,
        [ventureId]: loadedMentors,
      }));
    },
    [],
  );

  const handleFoundersLoaded = useCallback(
    (ventureId: Venture['id']) => (loadedFounders: Founder[]) => {
      setVentures((prevVentures) =>
        prevVentures.map((venture) =>
          venture.id === ventureId
            ? { ...venture, assignedFounders: loadedFounders.length }
            : venture,
        ),
      );
      setLoadedFoundersById((prevState) => ({
        ...prevState,
        [ventureId]: loadedFounders,
      }));
    },
    [],
  );

  const assignMentorsSuccessForm = useCallback(
    (ventureId: Venture['id'], handleModalClose: () => any) =>
      async ({
        assigned,
        removed,
        all,
      }: {
        assigned: string[];
        removed: string[];
        all: Mentor[];
      }) => {
        if (!assigned.length && !removed.length) {
          return handleModalClose();
        }

        try {
          setIsMentorsAssigningVenture((prev) => ({
            ...prev,
            [ventureId]: true,
          }));
          const assignedRequests = assigned.map(
            (mentorId) => () => venturesAPI.assignMentor(ventureId, mentorId),
          );
          const removedRequests = removed.map(
            (mentorId) => () =>
              venturesAPI.removeMentorAssignment(ventureId, mentorId),
          );
          await Promise.all([
            massAsyncRequest(assignedRequests),
            massAsyncRequest(removedRequests),
          ]);
          handleMentorsLoaded(ventureId)(all);
          handleModalClose();
          setIsMentorsAssigningVenture((prev) => ({
            ...prev,
            [ventureId]: false,
          }));
        } catch (e: any) {
          console.error('error', e);
        }
      },
    [handleMentorsLoaded],
  );

  const assignFoundersSuccessForm = useCallback(
    (ventureId: Venture['id'], handleModalClose: () => any) =>
      async ({
        assigned,
        removed,
        all,
      }: {
        assigned: string[];
        removed: string[];
        all: Founder[];
      }) => {
        if (!assigned.length && !removed.length) {
          return handleModalClose();
        }

        try {
          setIsFoundersAssigningVenture((prev) => ({
            ...prev,
            [ventureId]: true,
          }));
          const assignedRequests = assigned.map(
            (founderId) => () =>
              venturesAPI.assignFounder(ventureId, founderId),
          );
          const removedRequests = removed.map(
            (founderId) => () =>
              venturesAPI.removeFounderAssignment(ventureId, founderId),
          );
          await Promise.all([
            massAsyncRequest(assignedRequests),
            massAsyncRequest(removedRequests),
          ]);
          handleFoundersLoaded(ventureId)(all);
          handleModalClose();
          setIsFoundersAssigningVenture((prev) => ({
            ...prev,
            [ventureId]: false,
          }));
        } catch (e: any) {
          const messageError = e.response?.data?.message;

          enqueueSnackbar(
            'An error occurred while assigning founders. Please, try again.',
            {
              content: (key, message) =>
                SnackMessage({
                  key,
                  message,
                  variant: 'error',
                  additionalMessage: messageError,
                }),
              variant: 'error',
            },
          );
        }
      },
    [handleFoundersLoaded, enqueueSnackbar],
  );

  const checkSchedulingWizardForMentor = useCallback(
    async (venture: Venture) => {
      if (isMentor) {
        let alreadyLoadedMentors = loadedMentorsById[venture.id];

        if (!alreadyLoadedMentors) {
          alreadyLoadedMentors = await mentorsAPI.getMentorsDetailsByVentureId(
            venture.id,
          );
          setLoadedMentorsById((prevState) => ({
            ...prevState,
            [venture.id]: alreadyLoadedMentors,
          }));
        }

        const isMentorAssigned = alreadyLoadedMentors.some(
          (m) => m.id === tokenData?.identityid,
        );

        if (!isMentorAssigned) {
          setShowMentorSchedulingWarning(true);
          return;
        }
      }

      setSchedulingWizardTargetVenture(venture);
    },
    [isMentor, loadedMentorsById, tokenData?.identityid],
  );

  useEffect(() => {
    if (isMentor) {
      const loadMentor = async (id: MentorId) => {
        try {
          const loadedMentor = await mentorsAPI.getMentor(id);
          setMentor(loadedMentor);
        } catch (e: any) {
          if (e.response.status === 401) {
            console.error(e);
          } else {
            enqueueSnackbar(`Could not load ${rb('mentor')} details`, {
              variant: 'error',
            });
          }
        }
      };

      loadMentor(tokenData?.identityid as MentorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMentor, tokenData?.identityid]);

  useEffect(() => {
    getActiveCohorts()
      .then((vs) =>
        vs.map((v) => ({
          value: v.id,
          label: v.name,
        })),
      )
      .then((vs) => setCohorts([{ label: 'All groups', value: null }, ...vs]))
      .catch(() =>
        enqueueSnackbar('Could not load groups details', { variant: 'error' }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: VenturesContextProps = {
    ventures,
    mentor,
    cohorts,
    selectedVentureIds,
    loadedMentorsById,
    loadedFoundersById,
    isMentorsAssigningVenture,
    isFoundersAssigningVenture,
    isSendingProfileUpdate,
    mentorsAssignmentTargetVenture,
    foundersAssignmentTargetVenture,
    reportLinkTargetVentureIds,
    profileUpdateTargetVentureIds,
    schedulingWizardTargetVenture,
    showMentorSchedulingWarning,
    setVentures,
    setMentor,
    setCohorts,
    setSelectedVentureIds,
    setLoadedMentorsById,
    setLoadedFoundersById,
    setIsMentorsAssigningVenture,
    setIsFoundersAssigningVenture,
    setIsSendingProfileUpdate,
    handleVote,
    sendVentureUpdate,
    sendVentureUpdateProfiles,
    handleMentorsLoaded,
    handleFoundersLoaded,
    assignMentorsSuccessForm,
    assignFoundersSuccessForm,
    setMentorsAssignmentTargetVenture,
    setFoundersAssignmentTargetVenture,
    setReportLinkTargetVentureIds,
    setProfileUpdateTargetVentureIds,
    setSchedulingWizardTargetVenture,
    setShowMentorSchedulingWarning,
    checkSchedulingWizardForMentor,
  };

  return (
    <VenturesContext.Provider value={contextValue}>
      {children}
    </VenturesContext.Provider>
  );
};

export const useVenturesContext = () => {
  const context = useContext(VenturesContext);
  if (!context) {
    throw new Error(
      'useVenturesContext must be used within a VenturesProvider',
    );
  }
  return context;
};
